import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { effectiveAccess } from '../data/sectionAccess';
import {
  Calculator,
  BarChart3,
  MapPin,
  Package,
  BriefcaseBusiness,
  ClipboardCheck,
  HardHat,
  Trophy,
  Users,
  FileText,
  Pencil,
  Euro,
  ArrowRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Parse une date FR (JJ/MM/AAAA) ou ISO ; vrai si la Date FDC est déjà échue (renouvellement urgent).
const parseFRDate = (s) => {
  if (!s) return null;
  const str = String(s).trim();
  let m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = str.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};
const isFdcPast = (s) => { const d = parseFRDate(s); if (!d) return false; const t = new Date(); t.setHours(0, 0, 0, 0); return d < t; };

// Catalogue des modules — ordre et style conformes à la maquette du tableau de bord.
// variant 'dark' = carte graphite (modules prioritaires) · 'light' = carte blanche (modules secondaires).
const MODULES = [
  { to: '/simulations', accessKey: '/simulation/new', icon: Calculator, variant: 'dark', title: 'Simulations CAPEX', legend: "dossiers en cours d'étude", metric: 'simEnCours', badgeMetric: 'simATraiter' },
  { to: '/golden-info', icon: Trophy, variant: 'dark', title: "Info d'Or", legend: 'opportunités à qualifier', metric: 'infoOr', badgeMetric: 'infoOr' },
  { to: '/ca-corner', icon: BarChart3, variant: 'dark', title: 'CA Corner', legend: 'alertes vs N-1', metric: 'caUrgent', badgeMetric: 'caUrgent' },
  { to: '/carte-corners', icon: MapPin, variant: 'light', title: 'Carte des Corners', legend: 'secteurs · tournées IA', metric: 'secteurs' },
  { to: '/capex-sales', icon: Users, variant: 'dark', title: 'CAPEX Sales', legend: 'actions à mener', metric: 'csActions', badgeMetric: 'csActions' },
  { to: '/business-commercial', icon: BriefcaseBusiness, variant: 'dark', title: 'Business Commercial', legend: 'RDV la semaine passée', metric: 'businessRdv' },
  { to: '/primes', icon: Euro, variant: 'dark', title: 'Primes', legend: 'primes immédiates', metric: 'primesImmediates', format: 'euro' },
  { to: '/rapports', icon: FileText, variant: 'light', title: 'Rapports', legend: 'rapports prêts', metric: 'rapports' },
  { to: '/croquis-agenceur', icon: Pencil, variant: 'dark', title: 'Croquis Agenceur', legend: 'retour DSAO à valider', metric: 'croquis', badgeMetric: 'croquis' },
  { to: '/chantiers', icon: HardHat, variant: 'light', title: 'Pilotage Chantier', legend: 'chantiers suivis', metric: 'chantiers' },
  { to: '/dossiers-agenceur', icon: Package, variant: 'light', title: 'Commande Corner', legend: 'commandes agenceur', metric: 'commandes' },
  { to: '/pv-livraison', icon: ClipboardCheck, variant: 'light', title: 'PV de Livraison', legend: 'PV émis', metric: 'pv' },
];

const fmtEuro = (n) => `${Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;

export const DashboardModules = ({ toProcessCount = 0, metrics = {} }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const modules = useMemo(() => {
    const role = user?.role;
    const flags = { caCornerAccess: !!user?.ca_corner_access, isAdmin: role === 'admin' || role === 'super_admin' };
    return MODULES.filter((m) => effectiveAccess(role, m.accessKey || m.to, user?.section_access, flags));
  }, [user]);

  // Badge live « renouvellement urgent » CA Corner : nb de corners dont la Date FDC est échue.
  const [caUrgentCount, setCaUrgentCount] = useState(0);
  const hasCaCorner = useMemo(() => modules.some((m) => m.to === '/ca-corner'), [modules]);

  useEffect(() => {
    if (!hasCaCorner) return;
    const token = localStorage.getItem('capex_token');
    fetch(`${BACKEND_URL}/api/ca-corner/corners`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        const list = d.corners || [];
        setCaUrgentCount(list.filter((c) => isFdcPast(c.date_fdc)).length);
      })
      .catch(() => {});
  }, [hasCaCorner]);

  if (!modules.length) return null;

  // Valeurs live disponibles ; les autres restent indéfinies (tuile de navigation sans nombre).
  const data = {
    simATraiter: toProcessCount,
    caUrgent: caUrgentCount,
    ...metrics,
  };

  return (
    <section className="mt-6" data-testid="dashboard-modules">
      <h2 className="font-display font-bold uppercase text-ink text-[22px] tracking-[0.02em] mb-4">Vos modules</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {modules.map((m) => {
          const Icon = m.icon;
          const dark = true; // toutes les tuiles en fond graphite (uniforme)
          const badgeVal = m.badgeMetric ? data[m.badgeMetric] : undefined;
          const value = data[m.metric];
          const hasValue = value !== undefined && value !== null;
          const displayValue = hasValue ? (m.format === 'euro' ? fmtEuro(value) : value) : null;
          return (
            <button
              key={m.to}
              type="button"
              onClick={() => navigate(m.to)}
              className={`group relative overflow-hidden text-left rounded-scene p-5 min-h-[200px] flex flex-col justify-between transition-all duration-150 hover:-translate-y-[3px] ${
                dark
                  ? 'bg-graphite-900 hover:shadow-[0_12px_28px_rgba(20,20,22,0.35)]'
                  : 'bg-surface hover:shadow-[0_0_0_2px_#25272B,0_12px_24px_rgba(37,39,43,0.12)]'
              }`}
              data-testid={`module-btn-${m.to.replace(/\//g, '')}`}
            >
              {/* Filigrane d'icône (tuiles graphite) */}
              {dark && (
                <Icon className="pointer-events-none absolute -bottom-5 -right-4 w-36 h-36 text-white/[0.05]" strokeWidth={1} aria-hidden="true" />
              )}

              {/* Haut : icône + badge */}
              <div className="relative flex items-start justify-between">
                <span className={`w-[46px] h-[46px] rounded-ctrl grid place-items-center ${dark ? 'bg-graphite-700' : 'bg-graphite-900'}`}>
                  <Icon className="w-[22px] h-[22px] text-orange" strokeWidth={1.9} />
                </span>
                {badgeVal > 0 && (
                  <span
                    className="inline-flex items-center h-7 px-2.5 rounded-full bg-orange text-orange-on text-[12px] font-bold"
                    data-testid={`module-badge-${m.to.replace(/\//g, '')}`}
                  >
                    {badgeVal} à traiter
                  </span>
                )}
              </div>

              {/* Titre */}
              <h3 className={`relative font-display font-bold uppercase text-[21px] leading-tight mt-4 ${dark ? 'text-white' : 'text-ink'}`}>
                {m.title}
              </h3>

              {/* Bas : grand nombre + légende + flèche */}
              <div className="relative flex items-end justify-between mt-3">
                <div className="min-w-0">
                  {displayValue !== null && (
                    <p className={`font-display font-semibold tabular-nums leading-none ${m.format === 'euro' ? 'text-[28px]' : 'text-[34px]'} ${dark ? 'text-white' : 'text-ink'}`}>
                      {displayValue}
                    </p>
                  )}
                  <p className={`text-[13px] mt-1 leading-snug ${dark ? 'text-ondark-2' : 'text-text-3'}`}>{m.legend}</p>
                </div>
                <span className={`flex-none w-9 h-9 rounded-full grid place-items-center transition-colors ${
                  dark
                    ? 'bg-graphite-700 text-white group-hover:bg-orange group-hover:text-orange-on'
                    : 'bg-bg text-graphite-900 group-hover:bg-orange group-hover:text-orange-on'
                }`}>
                  <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default DashboardModules;
